import { LineChart } from '@talos/kyoko';
import type {
  AxisLabelsFormatterContextObject,
  Options,
  SeriesLineOptions,
  TooltipFormatterContextObject,
} from 'highcharts';
import { useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';

interface SignalsLineChartProps {
  series: SeriesLineOptions[];
  /** Formats y-axis tick labels. Defaults to `toLocaleString`. */
  formatValue?: (v: number) => string;
  /** Formats tooltip values. Defaults to `formatValue` when not provided. */
  formatTooltipValue?: (v: number) => string;
  /** Maps a series name to a display label in the tooltip. Defaults to the raw name. */
  formatSeriesName?: (name: string) => string;
}

const CHART_WRAPPER_PROPS = { h: '100%', w: '100%' } as const;

function defaultFormatValue(v: number): string {
  return v.toLocaleString();
}

/** Shared timeseries line chart for all Signals modules. */
export function SignalsLineChart({
  series,
  formatValue = defaultFormatValue,
  formatTooltipValue,
  formatSeriesName,
}: SignalsLineChartProps) {
  const theme = useTheme();

  // Refs let the memo capture stable closures while always reading the latest formatter.
  // This means callers can safely pass inline functions without triggering chart re-renders.
  const formatValueRef = useRef(formatValue);
  formatValueRef.current = formatValue;
  const formatTooltipValueRef = useRef(formatTooltipValue ?? formatValue);
  formatTooltipValueRef.current = formatTooltipValue ?? formatValue;
  const formatSeriesNameRef = useRef(formatSeriesName);
  formatSeriesNameRef.current = formatSeriesName;

  const options = useMemo<Options>(
    () => ({
      chart: {
        marginRight: 52, // keeps y-axis labels outside the plot area
        marginBottom: 15,
        zooming: { type: 'x', mouseWheel: { enabled: true } },
      },
      yAxis: {
        opposite: true,
        title: { text: undefined },
        labels: {
          align: 'left',
          x: 8,
          formatter(this: AxisLabelsFormatterContextObject): string {
            return formatValueRef.current(this.value as number);
          },
          style: { color: theme.colorTextDefault },
        },
      },
      plotOptions: {
        line: {
          marker: { enabled: false, states: { hover: { enabled: true } } },
        },
      },
      tooltip: {
        formatter(this: TooltipFormatterContextObject): string {
          const date = new Date(this.x as number).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          const label = formatSeriesNameRef.current ? formatSeriesNameRef.current(this.series.name) : this.series.name;
          return `<b>${date}</b><br/>${label}: ${formatTooltipValueRef.current(this.y as number)}`;
        },
      },
    }),
    [theme.colorTextDefault]
  );

  return <LineChart series={series} options={options} sizeAware showNoData wrapperProps={CHART_WRAPPER_PROPS} />;
}
